import { CredentialInformation, WebAuthnKeyAuthenticate, WebAuthnKeyRegister } from "@preveil-api";
import { AccountUserKey, AppConfiguration, AuthenticationTypes, GlobalConstants, SettingsErrorMessages, WebAuthnErrorMessages, Helpers } from "src/common";
import * as cbor from "cbor";

// ----------------------------------------------------------------------------
// SETTINGS HELPERS
// ----------------------------------------------------------------------------

// Description: Authenticates an existing WebAuthn Key with the browser
// https://webauthn.guide/#registration
export async function authenticateWebAuthnKey(cred_info: CredentialInformation, use_new_challenge?: boolean): Promise<WebAuthnKeyAuthenticate | undefined> {
  const config = new AppConfiguration();
  const decoded_context = JSON.parse(atob(cred_info.context));
  const allowCredentials: PublicKeyCredentialDescriptor[] = [
    {
      id: Uint8Array.from(Helpers.b64Decode(decoded_context.id)), // This should be the stored credential ID from your backend
      type: "public-key",
      transports: decoded_context.type === AuthenticationTypes.SECURITY_KEY ? ["usb", "nfc", "ble"] : decoded_context.type === AuthenticationTypes.PASSKEY ? config.browser === "Firefox" ? [] : ["hybrid" as AuthenticatorTransport] : [] // hybrid does not exist on firefox.
    }
  ];
  const challenge = use_new_challenge ? cred_info.newchallenge : cred_info.challenge;
  const publicKeyCredentialRequestOptions = {
    challenge: Helpers.b64Decode(challenge), // will get this from the backend
    timeout: GlobalConstants.WEB_AUTH_TIMEOUT, // Default: 2 minutes
    allowCredentials,
    authenticatorSelection: {
      authenticatorAttachment: "cross-platform" as AuthenticatorAttachment, // "platform" (device) or "cross-platform" (external keys)
      residentKey: "preferred" as ResidentKeyRequirement,
      requireResidentKey: false,
      userVerification: "preferred" as UserVerificationRequirement, // "required", "preferred", or "discouraged"
    }
  };
  const credential = await navigator.credentials.get({ publicKey: publicKeyCredentialRequestOptions });
  if (!!credential) {
    const public_credential = credential as PublicKeyCredential;
    const info = public_credential.response as AuthenticatorAssertionResponse;
    const params: WebAuthnKeyAuthenticate = {
      client_data: Helpers.b64Encode(new Uint8Array(info.clientDataJSON)),
      auth_data: Helpers.b64Encode(new Uint8Array(info.authenticatorData)),
      signature: Helpers.b64Encode(new Uint8Array(info.signature)),
    };
    return params;
  }
}

// Description: Registers a new Web Authn Key with the browser
// https://webauthn.guide/#registration
export async function registerWebAuthnKey(user_id: string, display_name: string): Promise<WebAuthnKeyRegister | undefined> {
  const user_id_byes = Helpers.b64Decode(btoa(user_id));
  const _id = new Uint8Array(64);
  _id.set(user_id_byes.slice(0, 32), 0);
  _id.set(await Helpers.sha256Checksum(user_id_byes), 32);

  const publicKey = {
    challenge: Uint8Array.from(window.crypto.getRandomValues(new Uint8Array(32)), c => c).buffer,
    // Relying Party (RP) information (your application)
    rp: {
      name: "PreVeil", // Human-readable name of your app
      id: window.location.hostname,
    },
    // User information (the person registering the passkey)
    user: {
      id: _id, // A unique ID for the user
      name: user_id, // User's email or username
      displayName: display_name, // Friendly display name
    },
    // Supported public key algorithms (most common: ES256 and RS256)
    pubKeyCredParams: [
      { type: "public-key", alg: -7 },   // ES256 (Elliptic Curve P-256)
      { type: "public-key", alg: -257 } // RS256 (RSA 2048)
    ] as PublicKeyCredentialParameters[],
    timeout: GlobalConstants.WEB_AUTH_TIMEOUT, // Default: 2 minutes
    authenticatorSelection: {
      authenticatorAttachment: "cross-platform" as AuthenticatorAttachment, // "platform" (device) or "cross-platform" (external keys)
      residentKey: "preferred" as ResidentKeyRequirement,
      requireResidentKey: false,
      userVerification: "preferred" as UserVerificationRequirement, // "required", "preferred", or "discouraged"
    },
    mediation: "required" as AuthenticatorSelectionCriteria,
    attestation: "direct" as AttestationConveyancePreference,
  };
  const credential = await navigator.credentials.create({ publicKey });
  if (!!credential) {
    const public_credential = credential as PublicKeyCredential;
    const _response = (credential as PublicKeyCredential).response as any; // as AuthenticatorAttestationResponse;
    const public_key = _response.getPublicKey();
    const public_key_algorithm = _response.getPublicKeyAlgorithm();
    let key_type: string = AuthenticationTypes.WEBAUTHN_CREDENTIAL;
    if (_response.getTransports().includes("internal") || _response.getTransports().includes("hybrid")) {
      key_type = AuthenticationTypes.PASSKEY;
    } else if (_response.getTransports().includes("usb") || _response.getTransports().includes("nfc") || _response.getTransports().includes("ble")) {
      key_type = AuthenticationTypes.SECURITY_KEY;
    }
    const context = {
      id: Helpers.b64Encode(new Uint8Array(public_credential.rawId)),
      type: key_type
    };
    const attestation_object = new Uint8Array((public_credential.response as AuthenticatorAttestationResponse).attestationObject);
    const decoded_attestation = !!attestation_object && attestation_object.length > 0 ? cbor.decode(attestation_object) : undefined;
    const auth_data = !!decoded_attestation ? new Uint8Array(decoded_attestation.authData) : undefined;
    const sign_count = !!auth_data ? new DataView(auth_data.buffer, 33, 4).getUint32(0, false) : 0;
    if (!!public_key && !!public_key_algorithm) {
      const payload = {
        public_key: Helpers.b64Encode(new Uint8Array(public_key)),
        public_key_algorithm,
        context: btoa(JSON.stringify(context)),
        sign_count,
        check_sign_count: sign_count > 0
      };
      return payload;
    }
  }
}

// Description: Get the error message that corresponds to the web auth API error.
export function getWebAuthErrorMessage(error: any): string {
  switch (error.name) {
    case "AbortError":
      return WebAuthnErrorMessages.abort_error;
    case "SecurityError":
      return WebAuthnErrorMessages.security_error;
    case "InvalidStateError":
      return WebAuthnErrorMessages.invalid_state_error;
    case "ConstraintError":
    case "QuotaExceededError":
      return WebAuthnErrorMessages.quota_exceeded;
    case "NetworkError":
      return WebAuthnErrorMessages.network_error;
    case "NotAllowedError":
    case "NotSupportedError":
      return WebAuthnErrorMessages.not_allowed_error;
    default:
      return SettingsErrorMessages.default;
  }
}

export async function encryptPhoneNumber(phone_number: string, user_key: AccountUserKey): Promise<string | undefined> {
  try {
    const encoded = Helpers.utf8Encode(phone_number);
    return Helpers.b64Encode(await user_key.encryption_key.seal(encoded));
  } catch (error) {
    console.error("Failed to encrypt phone number:", error);
    return undefined;
  }
}
